import * as io from "io-ts";
import { FragmentSlot, OptionV2 as Option } from "../Option/v2";
import Button from "../Button";
import { CheckLine, EqualizerLine } from "../IconV2/react";
import Menu from "../Menu";
import React, {
  createContext,
  useContext,
  useDeferredValue,
  useState,
  type ComponentProps,
  type PropsWithChildren,
} from "react";
import createGlobalState from "../lib/createGlobalState";
import { colors } from "../Theme";
import { Text } from "../Text";
import t from "../i18n";
import { lineHeights } from "..";
import Checkbox from "../Checkbox";
import { css } from "@linaria/core";

type DisplayMenu = "groupby" | "columns" | null;

const useTableGroupBy = createGlobalState({
  storage: "local",
  key: "table-group-by",
  type: io.union([io.string, io.null]),
  scoped: true,
});

export function useTableGroupingResults<T, C extends string>({
  items,
  groupBy,
  compareBy,
  scope,
  groupByDefault,
}: {
  items: T[];
  groupBy: { [key in C]: (item: T) => unknown };
  compareBy?: { [key in C]: <U>(a: U, b: U) => number };
  scope: string;
  groupByDefault?: keyof typeof groupBy;
}) {
  const groupedBy = useTableGroupBy({ scope, defaultValue: groupByDefault ?? null })[0] as C | null;

  if (groupedBy !== null && !Object.hasOwn(groupBy, groupedBy)) {
    throw new Error("invalid group by");
  }

  const compare = groupedBy ? compareBy?.[groupedBy] : null;

  const groupedItems =
    groupedBy === null
      ? new Map([[null, items]])
      : items.reduce<Map<unknown, T[]>>((acc, item) => {
          const key = groupBy[groupedBy](item);
          if (acc.has(key)) {
            acc.get(key)?.push(item);
          } else {
            acc.set(key, [item]);
          }
          return acc;
        }, new Map());

  const sortedGroupedItems = Array.from(groupedItems.entries()).sort(([aGroup], [bGroup]) => {
    /** @note keep items with no explicit group at the bottom of the list */
    if (aGroup === null) return 1;
    if (bGroup === null) return -1;

    /** @todo custom comparator */
    return compare?.(aGroup, bGroup) ?? 0;
  });

  const deferGroupedItems = useDeferredValue(sortedGroupedItems);
  const deferGroupedBy = useDeferredValue(groupedBy);

  return {
    groupedItems: deferGroupedItems,
    groupedBy: deferGroupedBy,
  };
}

const TableDisplayContext = createContext<{
  openedSubMenu: DisplayMenu;
  openSubMenu: (name: DisplayMenu) => void;
}>({
  openedSubMenu: null,
  openSubMenu: () => {},
});

export const TableDisplayMenu = ({
  children,
}: {
  children: ComponentProps<typeof Menu>["overlay"];
}) => {
  const [openedSubMenu, setOpenedSubMenu] = useState<DisplayMenu>(null);

  return (
    <TableDisplayContext.Provider value={{ openedSubMenu, openSubMenu: setOpenedSubMenu }}>
      <Menu onMouseLeave={() => setOpenedSubMenu(null)} overlay={children}>
        <Button leftIcon={<EqualizerLine />} variant="tertiary" slim>
          {t("table.display")}
        </Button>
      </Menu>
    </TableDisplayContext.Provider>
  );
};

const TableGroupByContext = createContext<{
  groupBy: ReturnType<typeof useTableGroupBy>[0];
  setGroupBy: ReturnType<typeof useTableGroupBy>[1];
}>({
  groupBy: "none",
  setGroupBy: () => {},
});

export function TableGroupByMenu<C extends string>({
  children,
  label,
  scope,
  groupByDefault,
}: {
  children: ComponentProps<typeof Menu>["overlay"];
  label: string | Partial<{ [key in C]: string }>;
  scope: string;
  groupByDefault?: C | null;
}) {
  const { openedSubMenu, openSubMenu } = useContext(TableDisplayContext);
  const [groupBy, setGroupBy] = useTableGroupBy({ scope, defaultValue: groupByDefault ?? null });
  return (
    <TableGroupByContext.Provider value={{ groupBy, setGroupBy }}>
      <Menu
        overlay={children}
        placement="right-start"
        opened={openedSubMenu === "groupby"}
        setOpened={() => openSubMenu("groupby")}
        onMouseLeave={() => openSubMenu(null)}
      >
        <Option
          className={groupByOption}
          onMouseEnter={() => openSubMenu("groupby")}
          withRightArrow
        >
          <FragmentSlot slot="text">{t("table.groupby")}</FragmentSlot>
          <Text
            slot="extra"
            type="body3"
            style={{ color: colors.carbon700, lineHeight: lineHeights[2], whiteSpace: "nowrap" }}
          >
            {typeof label === "string" ? label : label[groupBy as keyof typeof label]}
          </Text>
        </Option>
      </Menu>
    </TableGroupByContext.Provider>
  );
}

export function TableGroupByOption<C extends string>({
  value,
  children,
}: PropsWithChildren<{ value: C | null }>) {
  const { groupBy, setGroupBy } = useContext(TableGroupByContext);
  const selected = groupBy === value;
  return (
    <Option onClick={() => setGroupBy(value)} selected={selected}>
      <FragmentSlot slot="text">{children}</FragmentSlot>
      {selected && (
        <FragmentSlot slot="extra">
          <CheckLine color={colors.lightNitrogen500_Main} />
        </FragmentSlot>
      )}
    </Option>
  );
}

const useHiddenTableColumns = createGlobalState({
  storage: "local",
  key: "table-hidden-column",
  type: io.array(io.string),
  scoped: true,
});

const useHiddenTableColumnPristine = createGlobalState({
  storage: "local",
  key: "table-hidden-column-pristine",
  type: io.boolean,
  defaultValue: true,
  scoped: true,
});

const HiddenColumnContext = createContext<{
  pristine: ReturnType<typeof useHiddenTableColumnPristine>[0];
  setPristine: ReturnType<typeof useHiddenTableColumnPristine>[1];
  hiddenColumns: ReturnType<typeof useHiddenTableColumns>[0];
  setHiddenColumns: ReturnType<typeof useHiddenTableColumns>[1];
}>({
  pristine: true,
  setPristine: () => {},
  hiddenColumns: [],
  setHiddenColumns: () => {},
});

export function useTableHiddenColumns<T, C extends string>({
  scope,
  items,
  predicates,
}: {
  scope: string;
  items: T[];
  predicates: { [key in C]: (item: T) => boolean };
}): { hiddenColumns: string[]; autoHiddenColumns: string[] } {
  const [pristine] = useHiddenTableColumnPristine({ scope });
  const [hiddenColumns] = useHiddenTableColumns({
    scope,
    defaultValue: [],
  });

  const autoHiddenColumns = Object.keys(predicates).filter(column => {
    const predicate = predicates[column as keyof typeof predicates];
    return items.every(item => predicate?.(item) ?? false);
  });

  const result = Array.from(new Set([...(pristine ? [] : hiddenColumns), ...autoHiddenColumns]));

  return { hiddenColumns: result, autoHiddenColumns };
}

export function TableColumnsMenu({
  children,
  scope,
}: {
  children: ComponentProps<typeof Menu>["overlay"];
  scope: string;
}) {
  const { openedSubMenu, openSubMenu } = useContext(TableDisplayContext);
  const [pristine, setPristine] = useHiddenTableColumnPristine({ scope });
  const [hiddenColumns, setHiddenColumns] = useHiddenTableColumns({
    scope,
    defaultValue: [],
  });

  const options = Array.isArray(children) ? children : [children];
  const count = options.filter(opt => !opt.props.hidden).length;
  const max = options.length;

  return (
    <HiddenColumnContext.Provider
      value={{ hiddenColumns, setHiddenColumns, pristine, setPristine }}
    >
      <Menu
        overlay={children}
        placement="right-start"
        opened={openedSubMenu === "columns"}
        setOpened={() => openSubMenu("columns")}
        onMouseLeave={() => openSubMenu(null)}
      >
        <Option onMouseEnter={() => openSubMenu("columns")} withRightArrow>
          <FragmentSlot slot="text">{t("table.columns")}</FragmentSlot>
          <Text
            slot="extra"
            type="body3"
            style={{ color: colors.carbon700, lineHeight: lineHeights[2] }}
          >
            {count}/{max}
          </Text>
        </Option>
      </Menu>
    </HiddenColumnContext.Provider>
  );
}

export function TableColumnsOption<C extends string>({
  value,
  disabled,
  hidden,
  children,
}: PropsWithChildren<{
  value: C;
  hidden: boolean;
  disabled?: ComponentProps<typeof Option>["disabled"];
}>) {
  const { setHiddenColumns, pristine, setPristine } = useContext(HiddenColumnContext);
  const selected = !hidden;

  function toggle(selected: boolean) {
    if (pristine) setPristine(false);
    setHiddenColumns(columns =>
      selected ? Array.from(new Set([...columns, value])) : columns.filter(c => c !== value),
    );
  }

  return (
    <Option onClick={() => toggle(selected)} selected={selected} disabled={disabled}>
      <Checkbox
        slot="checkbox"
        smallIndicator
        checked={selected}
        disabled={disabled}
        onChange={() => {}}
      />
      <FragmentSlot slot="text">{children}</FragmentSlot>
    </Option>
  );
}

const groupByOption = css`
  min-width: 300px;
`;
